import { FormatHelpers } from "../helpers/format-helpers";
import { StringHelpers } from "../helpers/string-helpers";
import { TraceLoggingHelpers } from "../helpers/trace-logging-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { CreateLimitAlertModel } from "../models/limit-alert/create-limit-alert.model";
import { IComponent } from "./component.interface";
import { BuySellButtonWithCurrentPriceMenuComponent } from "./menu/buy-sell-button-with-current-price-menu.component";

export class MainSymbolCurrentPriceComponent implements IComponent {
    public id = StringHelpers.newGuid();

    private element: HTMLElement;
    private addButton: HTMLElement | null = null;
    private menuItems: HTMLElement | null = null;
    private wrapper: HTMLElement | null = null;
    private createPriceAlertMenu: HTMLElement | null = null;

    private base: IChartContainer;

    private addIconMouseInTimer: any;
    private pointMouseInTimer: any;
    private preventElementCloseTimer: any;

    private lastPrice = 0;
    private currentPriceValue = 0;
    private createPriceAlertPrice = 0;
    private elementTop = 0;
    private preventElementClose = false;
    private mouseInAddButton = false;
    private priceToString = '';

    constructor(element: HTMLElement, base: IChartContainer) {
        this.element = element;
        this.base = base;
        this.addButton = this.element.findFirst('.add');
        this.menuItems = this.base.element.findFirst('.main-symbol-current-price-menu-items');
        this.createPriceAlertMenu = this.menuItems?.findFirst('.menu-item.add-price-alert');
        this.wrapper = this.base.tiChartSeries.element.findFirst('.main-symbol-current-price');;

        this.subscribeChartService();
        this.bindAddIconEvents();
        this.onCreatePriceAlertClick();
        this.onBodyClick();
        this.onEscPress();
        this.onMouseEvents();
    }

    private display(x: number, y: number) {
        if (this.menuItems?.classList.contains('active')) return;

        const chartStyle = this.base.tiChartSeries?.chartStyleSettings;
        const nonChartMode = chartStyle?.nonNormalChartMode();
        if (nonChartMode)  return;

        clearTimeout(this.pointMouseInTimer);
        clearTimeout(this.addIconMouseInTimer);

        let active = true;
        this.base.tiChartSeries.chartElement.style.cursor = '';
        if (this.base.tiChartSeries.chartStyleSettings?.isComparisonSymbolPercentageMode || this.base.tiChartSeries.base.isPreview) {
            this.hide();
            return;
        }

        const crossHairXAxis = this.base.tiChartSeries.element.findFirst('.highchart-main-crosshair.x-axis');
        if (!this.wrapper) {
            this.hide();
            return;
        }

        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis ? this.base.tiChartSeries.highStockChart.yAxis[0] : null;
        if (!yAxis) {
            this.hide();
            return;
        }

        if (crossHairXAxis?.getAttribute('visibility') == 'hidden') {
            this.base.tiChartSeries.chartElement.style.cursor = '';
        } else if ((this.base.tiChartSeries.chartStyleSettings?.crossHairsEnabled ?? false)) {
            this.base.tiChartSeries.chartElement.style.cursor = 'crosshair';
        }

        let xAxisTop = 0;

        const parentElementRect = this.base.tiChartSeries.chartElement.getBoundingClientRect();

        xAxisTop = y - parentElementRect.y;
        const newPrice = yAxis.toValue(xAxisTop);
        const newPriceToValidateTop = yAxis.toValue(xAxisTop - 10);
        const newPriceToValidateBottom = yAxis.toValue(xAxisTop + 10);
        if (newPriceToValidateTop > yAxis.max || newPriceToValidateBottom < yAxis.min) {
            this.hide();
            this.dragStart = false;
            return;
        }

        const chartRect = this.base.tiChartSeries.chartElement.getBoundingClientRect();
        const mouseX = x;
        const distanceFromRight = chartRect.right - mouseX;
        let yAxisWidth = 70;
        const chartExtraSmall = this.base.isExtraSmall();
        if (this.base.tiChartSeries?.chartStyleSettings?.showSmartRiskLevel && !chartExtraSmall) {
            yAxisWidth = 100;
        }

        if (distanceFromRight <= yAxisWidth) {
            this.visibilityHidden();
        } else {
            this.visibilityVisible();
        }

        this.currentPriceValue = newPrice;
        if (this.currentPriceValue > 0) {
            this.enableAddButton();
        } else {
            this.disableAddButton();
        }
        
        const pElement = this.wrapper.findFirst('p');

        this.priceToString = '$' + FormatHelpers.numberToStringThousandSeparators(this.currentPriceValue, true);
        pElement?.setInnerHtml(this.priceToString);

        const myTop = this.wrapper.clientHeight;
        const extraTop = this.base.tiChartSeries.hasAnyComparisonSymbols() ? 20 : 0;
        xAxisTop += extraTop;

        this.elementTop = xAxisTop - myTop / 2;

        if (this.elementTop < 2) {
            this.elementTop = 2;
        }

        this.wrapper.style.top = this.elementTop + 'px';

        if (active) {
            this.wrapper.removeClass('hidden');
        }
    }

    public hide(ms?: number) {
        if (this.menuItems?.classList.contains('active')) return;

        clearTimeout(this.pointMouseInTimer);
        this.pointMouseInTimer = setTimeout(() => {
            if (this.mouseInAddButton) return;
            const wrapper = this.base.tiChartSeries.element.findFirst('.main-symbol-current-price');
            if (!wrapper) {
                return;
            }

            this.mouseInAddButton = false;
            this.visibilityVisible();
            wrapper.classList.add('hidden');
        }, ms >= 0 ? ms : 150);
    }

    private visibilityHidden() {
        this.wrapper.style.opacity = '0';
    }

    private visibilityVisible() {
        this.wrapper.style.opacity = '1';
    }

    public hideMenuItems() {
        this.menuItems?.classList.remove('active');
    }

    public hideAddButton() {
        this.addButton?.addClass('hidden');
    }

    public showAddButton() {
        this.addButton?.removeClass('hidden');
    }

    public disableAddButton() {
        this.addButton?.addClass('disabled');
    }

    public enableAddButton() {
        this.addButton?.removeClass('disabled');
    }

    private subscribeChartService() {
        this.base.chartService.subscribeLastPriceData((price: number) => {
            this.lastPrice = price;
        });
        this.base.chartService.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            if (chartStyle.showSmartRiskLevel) {
                this.element.classList.add('smartrisk');
            } else {
                this.element.classList.remove('smartrisk');
            }
            this.hideAddIconIfDemo(chartStyle);
        });
    }

    private hideAddIconIfDemo(chartStyle: ChartStyleSettingsModel) {
        if (!this.addButton) {
            return;
        }

        const shouldHideAddMenu = this.base.isDemo || !chartStyle.plusMenuEnabled || !chartStyle.tradeOrderEntrySupported || !chartStyle.tradeOrderEntryEnabled || !chartStyle.chartSettingsPopupModel?.defaultEnableTradeOrderEntry;
        if (shouldHideAddMenu) {
            this.addButton.classList.add('hidden');
        } else {
            this.addButton.classList.remove('hidden');
        }
    }

    private bindAddIconEvents() {
        if (!this.addButton) {
            return;
        }

        this.addButton.addEventListener('click', () => {
            if (!this.menuItems || this.addButton?.containClass('disabled')) {
                return;
            }

            this.createPriceAlertMenu?.findFirst('.label')?.setInnerHtml(this.base.tiChartSeries.symbol + ' at ' + this.priceToString);
            this.createPriceAlertPrice = this.currentPriceValue;

            this.setBuyAndSellPrice();
            this.setPreventElementClose(true);
            this.menuItems.addClass('active');

            let elementTop = this.elementTop + 20;
            if (elementTop + 40 > this.base.tiChartSeries.chartElement.clientHeight) {
                elementTop -= 64 + this.element.clientHeight;
            }

            this.menuItems.style.top = elementTop + 'px';

            this.setPreventElementClose(false);
        });

        this.addButton.addEventListener('mouseenter', () => {
            this.mouseInAddButton = true;
            clearTimeout(this.addIconMouseInTimer);
            clearTimeout(this.pointMouseInTimer);
        });
        this.element.addEventListener('mouseenter', () => {
            this.mouseInAddButton = true;
            clearTimeout(this.addIconMouseInTimer);
            clearTimeout(this.pointMouseInTimer);
        });
        this.element.addEventListener('mouseleave', () => {
            this.mouseInAddButton = false;
        });
        this.addButton.addEventListener('mouseleave', () => {
            this.addIconMouseInTimer = setTimeout(() => {
                this.mouseInAddButton = false;
                this.hide();
            }, 200);
        });
    }

    private onCreatePriceAlertClick() {
        this.createPriceAlertMenu?.addEventListener('click', () => {
            const requestPrice = parseFloat(this.createPriceAlertPrice.toFixed(2));
            this.base.chartService.requestCreatePriceAlert(new CreateLimitAlertModel({
                symbol: this.base.tiChartSeries.symbol,
                isLong: this.lastPrice < requestPrice,
                price: requestPrice
            }));

            this.menuItems?.removeClass('active');
        });
    }

    private setBuyAndSellPrice() {
        const comp = this.base.getComponentsByType(BuySellButtonWithCurrentPriceMenuComponent)?.[0] as BuySellButtonWithCurrentPriceMenuComponent;
        if (comp) {
            comp.reset();
            comp.updateValue(this.currentPriceValue);
            comp.onCloseCallback(() => {
                this.hideMenuItems();
                this.hide();
            });
        }
    }

    private setPreventElementClose(value: boolean) {
        if (value) {
            clearTimeout(this.preventElementCloseTimer);
            this.preventElementClose = value;
        } else {
            this.preventElementCloseTimer = setTimeout(() => {
                this.preventElementClose = false;
            }, 500);
        }
    }

    private onEscPress() {
        this.base.domEvents.onEscPress(this.id, () => {
            if (this.menuItems?.containClass('active')) {
                this.closeMenu();
            }
        });
    }

    private closeMenu() {
        this.menuItems?.findFirst('.trade-order-type')?.removeClass('active');
        this.menuItems?.removeClass('active');
    }

    private dragStart = false;
    private mouseLeaveTimer: any = 0;
    private mouseLeave() {
        clearTimeout(this.mouseLeaveTimer);
        this.mouseLeaveTimer = setTimeout(() => {
            if (!this.dragStart) {
                return;
            }

            this.dragStart = false;

            const pTag = this.wrapper?.querySelector<HTMLElement>('.last-price p');
            if (pTag)
                pTag.style.cursor = '';

            // this.element.style.opacity = '';
        }, 20);
    };

    private onMouseEvents() {
        let startY = 0;
        const moveThreshold = 10;
        this.wrapper.addEventListener('mousedown', (e: any) => {
            if (this.dragStart) {
                return;
            }

            this.dragStart = true;

            startY = e.clientY;

            const highchartContainer = this.base.tiChartSeries.element?.querySelector<HTMLElement>('.highcharts-container');
            if (highchartContainer) {
                highchartContainer.style.cursor = 'n-resize';
            }

            const pTag = this.wrapper?.querySelector<HTMLElement>('.last-price p');
            if (pTag)
                pTag.style.cursor = 'n-resize';

            // this.element.style.opacity = '0';
        });
        this.wrapper.addEventListener('mouseup', () => {
            this.mouseLeave();
        }); 
        this.base.tiChartSeries.element.addEventListener('mousemove',(ev: MouseEvent) => {
            if (this.dragStart && this.base.tiChartSeries?.chartStyleSettings?.yAxisPanningEnabled) {
                const currentMainChartElement = (ev.target as HTMLElement).closest('.main-chart');
                const threshold = this.base.tiChartSeries?.chartStyleSettings?.showSmartRiskLevel ? 100 : 70;
                if (!currentMainChartElement || ev.pageX < currentMainChartElement.clientWidth - threshold) {
                    this.dragStart = false;
                    return;
                }

                const currentY = ev.clientY;
                const diffY = currentY - startY;
                if (Math.abs(diffY) < moveThreshold) {
                    TraceLoggingHelpers.log('Dragging stopped due to small movement.');
                    return
                }

                TraceLoggingHelpers.log('MainSymbolCurrentPrice ' + diffY)
                if (diffY > 0) {
                    this.base.tiChartSeries?.expandYAxis(1);
                } else {
                    this.base.tiChartSeries?.expandYAxis(-1);
                }

                startY = currentY;
            }

        });
        document.body.addEventListener('mousemove', (ev: MouseEvent) => {
            let shouldDisplay = false;

            if (this.base.marketingWebChartMode) {
                if (this.base.pnpMode && this.base.tiChartSeries.element == (ev.target as HTMLElement).closest('.ti-chart.pnp') || this.base.chartWrapper == (ev.target as HTMLElement).closest('.web-charts-marketing-wrapper')) {
                    shouldDisplay = true;
                } else if (!!(ev.target as HTMLElement).closest('.profit-target-wrapper') ||
                    !!(ev.target as HTMLElement).closest('.stop-loss-wrapper') ||
                    this.base.tiChartSeries.preventDragging) {
                    shouldDisplay = true;
                }
            } else {
                shouldDisplay = true;
            }

            if (shouldDisplay) {
                this.display(ev.x, ev.y);
            } else {
                this.hide(0);
            }
        });
        document.body.addEventListener('mouseleave', (ev: MouseEvent) => {
            this.mouseLeave();
        });
    }

    private onBodyClick() {
        this.base.domEvents.onBodyClick(this.id, (ev: any) => {
            if (this.preventElementClose) {
                return;
            }

            if (!this.menuItems?.containClass('active')) {
                return;
            }

            const target = ev.target as HTMLElement;
            if (target.classList.contains('main-symbol-current-price-menu-items') || !!target.closest('.main-symbol-current-price-menu-items')) {
                return;
            }

            this.closeMenu();
        });
    }
}